import React from 'react';
import { captureException } from '../config/sentry';

// React Error Boundary (LOG-18). Catches any unhandled exception thrown
// during render in any descendant component and shows a friendly fallback
// instead of the React-default blank white screen. Class component because
// React's Error Boundary API requires componentDidCatch + getDerivedStateFromError,
// which don't have functional-component equivalents.
//
// LOG-20: componentDidCatch reports through config/sentry, which is a
// no-op unless VITE_SENTRY_DSN is set at build time.
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Kept regardless of whether reporting is on: the console is the only
    // signal when someone is debugging locally or looking over a
    // customer's shoulder. info.componentStack is the React-tree path to
    // the failing component.
    // eslint-disable-next-line no-console
    console.error('React error boundary caught:', error, info);

    captureException(error, { componentStack: info?.componentStack });
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: '60vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 20px',
          fontFamily: "'Outfit', sans-serif",
          color: 'var(--asg-text)',
          textAlign: 'center',
        }}>
          <div style={{ maxWidth: 460 }}>
            <h1 style={{
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              fontSize: '36px',
              fontWeight: 500,
              margin: '0 0 12px',
            }}>Something went wrong</h1>
            <p style={{ fontSize: '15px', lineHeight: 1.6, color: 'var(--asg-text-light)', margin: '0 0 24px' }}>
              The page hit an unexpected error. Try reloading; if it keeps happening, please contact us at <a href="mailto:info@asgshading.com.au" style={{ color: 'var(--asg-accent-dark)' }}>info@asgshading.com.au</a>.
            </p>
            <button
              type="button"
              onClick={this.handleReload}
              style={{
                padding: '12px 28px',
                background: 'var(--asg-cta)',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                fontFamily: 'inherit',
                fontSize: '13px',
                fontWeight: 600,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                cursor: 'pointer',
              }}
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
